// Guided walkthrough definitions — single source of truth for tour copy.
// Edit copy here without touching components (same convention as setupContent.js).
//
// Copy style: short. One idea per sentence, one or two sentences per paragraph.
// `body` accepts a string or an array of strings — arrays render as separate
// paragraphs so nothing reads as a run-on.
//
// Step shape:
//   target          null renders a centered card; otherwise a [data-tour="..."]
//                   selector that gets spotlit
//   targets         alternative to `target`: several [data-tour] names spotlit
//                   together as one union rectangle
//   optionalTargets [data-tour] names folded into the spotlight whenever they
//                   exist (e.g. a tooltip that appears on hover); never waited on
//   title/body      copy; '{brandName}' / '{boardName}' are replaced from the
//                   tour context
//   items           optional [{ label, text }] list rendered under the body —
//                   one line of value per feature, no clicking around
//   advance         'next'          button
//                   'click-target'  user must click the highlighted element
//                   'hover-target'  Next appears once the highlight is hovered
//                   'event'         advances when EventBus emits `event`; an
//                                   object payload is merged into the context
//   event           EventBus event name ('event' steps only)
//   hint            small text shown in place of the Next button on
//                   interaction steps (defaults per advance mode)
//   interactive     let the user interact with the spotlit element even on a
//                   'next' step (e.g. type into an input)
//   passthrough     don't block clicks outside the spotlight either (for
//                   controls whose dropdowns open outside the hole)
//   ctaLabel        label for the Next button
//   cardPlacement   'auto' (default: below → above → right → left), 'center',
//                   'right', 'left', 'above' or 'below'
//   waitForReady    hold the CTA until the target reports [data-tour-ready];
//                   true = every target, or a name / list of names
//   readyLabel      text shown next to the spinner while waiting
//   variant         'outro' renders the centered end-of-tour card (kicker +
//                   centered button, no skip / counter); 'completion' renders
//                   the end-of-tour card with a scene of the result in the
//                   wild (TourCompletionCard) — set `scene`, `pill`, `accent`
//   scene           TourCompletionCard scene key (see SCENES in TourCompletionCard)
//   pill            green status pill above the title ('completion' only)
//   accent          stage tint for 'completion': 'red' | 'blue' | 'purple' | 'black'.
//                   Also themes the tour's buttons (first step accent in the tour)
//   kicker          small label above the title ('outro' / 'completion')
//   suggestions     [{ label, value }] one-click chips rendered under the
//                   title (above body); clicking emits `suggestionEvent`
//   suggestionEvent EventBus event name fired by a suggestion chip
//   openFilterDropdown / openSortDropdown / openSidebarCreate
//                   when true, TourOverlay emits tour:step-enter so the
//                   matching UI opens for the spotlight
//   route           optional route to navigate to before the step shows;
//                   '{brandId}' in params is replaced from the tour context
//
// Tour shape: { id, returnToSetup, completesStep, steps }
//   returnToSetup  routes back to the Launchpad when the tour completes (not skipped)
//   completesStep  Launchpad checklist step id ticked off optimistically on
//                  completion, so the user sees it done the moment they land

// Every guided flow opens with the same intro modal (SetupTourIntroModal): it
// names the flow, says what's about to happen and how long it takes. Keys
// match the Launchpad row's `cta.tour` / `cta.flow` / `cta.modal`.
//
//   kicker  small label above the title
//   body    string or array of short paragraphs
//   steps   what happens, in order; the first one is highlighted as "now"
//   accent  'red' Spyder, 'blue' library, 'purple' Discovery, 'black' Lens
//   icon    animated product key (spyder / library / discovery / briefs / lens)
//           or a static badge key in SetupTourIntroModal (adRank / instagram / newBoard /
//           topPerforming / comparison / mcp)
//   video   optional { src, poster } tutorial shown in a side column
export const TOUR_INTROS = {
  'spyder-brand': {
    kicker: 'Guided walkthrough',
    title: 'Track and analyze every ad a brand is running',
    body: [
      'Spyder watches every ad a company launches and keeps the history.',
      "Let's start by previewing a popular brand in your niche."
    ],
    steps: [
      'Pick a brand to track',
      'See the data Spyder pulls in',
      'Return to your Launchpad to continue'
    ],
    ctaLabel: 'Pick a Brand',
    time: 'Under 1 minute',
    accent: 'red',
    icon: 'spyder'
  },
  'ad-rank': {
    kicker: 'Guided walkthrough',
    title: "You've seen the ads, now see what's winning.",
    body: [
      'Ad Rank reveals which ads are pulling the most impressions right now.',
      'So you know what is actually working, not just what exists.'
    ],
    steps: [
      'Open Ad Rank for {brandName}',
      'Understand how to see top ads, evergreen & breakout',
      'Get ready to save your first ad'
    ],
    ctaLabel: 'Open Ad Rank',
    time: 'Under 1 minute',
    accent: 'red',
    icon: 'adRank'
  },
  'save-ad': {
    kicker: 'Guided walkthrough',
    title: 'Save genius before you can share it',
    body: [
      "We'll open one of our own ads so you can see what Foreplay pulls out of every save: the transcript, the AI breakdown, the landing page.",
      'Then you save it.'
    ],
    steps: [
      'Open a sample ad on this page',
      'Break down the data you have access to',
      'Hit save, and it lands in your swipe file'
    ],
    ctaLabel: 'Open the Ad',
    time: 'About 1 minute',
    accent: 'blue',
    icon: 'library'
  },
  'board-share': {
    kicker: 'Guided walkthrough',
    title: 'Organize and share collections of inspiration',
    body: [
      "Name a board and we'll fill it with a few ads so there's something worth sending.",
      'Then you share the link — whoever opens it sees the live version.'
    ],
    steps: [
      'Name your board',
      'We add a few ads to it automatically',
      'You copy the share link and send it'
    ],
    ctaLabel: 'Create a Board',
    time: 'Under 1 minute',
    accent: 'blue',
    icon: 'newBoard'
  },
  'discovery-search': {
    kicker: 'Guided walkthrough',
    title: "Find winning ads you didn't know existed.",
    body: [
      'Discovery is the feed of 200M+ ads Foreplay has indexed, from 500K brands.',
      "Search a keyword, narrow it with filters, and save what's worth keeping."
    ],
    steps: [
      'Search a keyword in Discovery',
      'Learn filters and sorting, and how to save from the feed',
      'Return to your Launchpad to connect Instagram for mobile saving'
    ],
    ctaLabel: 'Open Discovery',
    time: 'Under 1 minute',
    accent: 'purple',
    icon: 'discovery'
  },
  instagram: {
    kicker: 'Guided walkthrough',
    title: 'Save ads from your phone with a DM.',
    body: [
      'Half the ads worth stealing show up in your own feed.',
      'Connect Instagram once and saving becomes forwarding a post to @foreplay_co.'
    ],
    steps: [
      'Add your Instagram handle and pick a board',
      'DM the code we give you to @foreplay_co',
      'Forward any ad from your feed and it lands in Foreplay'
    ],
    video: {
      src: 'https://publicassets.foreplay.co/ig-dm-tutorial.webm',
      poster: 'https://publicassets.foreplay.co/ig-dm-tutorial-thumbnail.png'
    },
    ctaLabel: 'Connect Instagram',
    time: 'Under 1 minute',
    accent: 'blue',
    icon: 'instagram'
  },
  // Without API access the CTA reads `unlockCtaLabel` and unlocks the API
  // (free credits) before moving on to the connect steps
  mcp: {
    kicker: 'Setup',
    title: 'Plug your AI into Foreplay.',
    body: [
      'The MCP gives Claude or ChatGPT the same 200M+ ads, your boards and your Lens data.',
      'It runs on the Foreplay API, which is free to unlock and comes with free credits.'
    ],
    steps: [
      'Pick Claude or ChatGPT',
      'Add Foreplay as a connector and sign in',
      'Come back and run a prompt from your Launchpad'
    ],
    ctaLabel: 'Continue',
    unlockCtaLabel: 'Unlock API',
    time: 'About 2 minutes',
    accent: 'black',
    icon: 'mcp'
  },
  'lens-connect': {
    kicker: 'Guided walkthrough',
    title: 'Connect your own ad account',
    body: [
      'Lens is read-only. It never spends, never publishes, and your data stays in your workspace.',
      "We'll open the creation flow so you can name a Lens brand and authenticate Meta or TikTok."
    ],
    steps: [
      'Name the brand and pick currency',
      'Authenticate Meta or TikTok',
      'Come back while the first sync runs (24–48 hours)'
    ],
    ctaLabel: 'Connect an Account',
    time: 'About 2 minutes',
    accent: 'black',
    icon: 'lens'
  },
  'lens-top-performing': {
    kicker: 'Guided walkthrough',
    title: 'Learn to use Top Performing reports',
    body: [
      'Use Top Performing when you want to view the creatives that are working across all campaigns and ad sets.',
      'This is a demo. The ads are ours, so you can learn the report with no account connected.'
    ],
    steps: [
      'See when to use Top Performing',
      'Group, chart and filter the table',
      'Customize the columns you care about'
    ],
    ctaLabel: 'Start the Demo',
    time: 'About 1 minute',
    accent: 'black',
    icon: 'topPerforming'
  },
  'lens-comparison': {
    kicker: 'Guided walkthrough',
    title: 'Learn Comparison reports',
    body: [
      'This demo compares three groups on dummy data — agency, external creators, and in-house — by tokens in the ad name. Nothing here is your account.',
      'A brand uses it to see how the agency is doing next to other sources. An agency uses it to show a client the same split.'
    ],
    steps: [
      'See how groups are defined',
      'Add or rename a group',
      'Open a row to see the ads inside'
    ],
    ctaLabel: 'Start the Demo',
    time: 'About 1 minute',
    accent: 'black',
    icon: 'comparison'
  }
}

// The ad showcased in the "Save Your First Ad" flow (one of Foreplay's own)
export const SHOWCASE_AD = {
  id: 'mLWwMBdW1fmcX9AxR27M',
  sharedBy: 'sf4MEW1LckbdxOFDfurphiaTY4e2'
}

export const TOURS = {
  'discovery-search': {
    id: 'discovery-search',
    returnToSetup: true,
    completesStep: 'discovery_search',
    steps: [
      {
        id: 'search',
        target: 'discovery-search',
        route: { name: 'DiscoveryView' },
        cardPlacement: 'below',
        title: 'Search a keyword',
        body: 'Something like a product, a benefit, or a phrase you would put in a hook.',
        suggestions: [
          { label: 'Try “sleep”', value: 'sleep' },
          { label: 'Try “protein”', value: 'protein' },
          { label: 'Try “hydration”', value: 'hydration' },
          { label: 'Try “collagen”', value: 'collagen' },
          { label: 'Try “skincare”', value: 'skincare' }
        ],
        suggestionEvent: 'discovery:search-keyword',
        advance: 'event',
        event: 'discovery:search-submitted',
        hint: 'Tap a chip or press Enter'
      },
      {
        id: 'filters',
        target: 'filter-add',
        optionalTargets: ['filter-add-dropdown'],
        passthrough: true,
        openFilterDropdown: true,
        cardPlacement: 'below',
        title: 'Narrow it down',
        body: [
          'Filter by format, platform, niche, language, how long the ad has run and more.',
          'Stack filters to get from millions of ads to the dozen that matter.'
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'sort',
        target: 'filter-sort',
        optionalTargets: ['filter-sort-dropdown'],
        passthrough: true,
        openSortDropdown: true,
        cardPlacement: 'below',
        title: 'Sort for winners',
        body: [
          'Most Relevant matches your search. Longest Running surfaces ads that have earned their spend.',
          'For You learns from what you save.'
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'save',
        target: 'ad-save',
        interactive: true,
        passthrough: true,
        cardPlacement: 'right',
        title: 'Save from the feed',
        body: [
          'Hover the card, then hit Save to Swipe File — or pick a board from the menu.',
          'Click the ad itself anytime to open the full breakdown.'
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'done',
        target: null,
        variant: 'completion',
        scene: 'discovery-search',
        pill: 'Search complete',
        accent: 'purple',
        kicker: 'Up next: Instagram',
        title: 'Discovery is yours',
        body: [
          'Every search covers 200M+ ads, updated as brands launch.',
          'Next, connect Instagram so you can save from your phone.'
        ],
        advance: 'next',
        ctaLabel: 'Back to Launchpad'
      }
    ]
  },

  // Walks through the ad details drawer on the showcase ad, ending on Save
  'save-ad': {
    id: 'save-ad',
    returnToSetup: false,
    steps: [
      {
        id: 'drawer',
        target: 'drawer-root',
        cardPlacement: 'center',
        title: 'The ad details drawer',
        body: [
          'Every ad in Foreplay opens here: the creative on the left, everything we know about it on the right.',
          "Let's walk through what you get with every save."
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'metadata',
        target: 'drawer-metadata',
        cardPlacement: 'left',
        title: 'The ad, decoded',
        body: [
          'Brand, format, platforms, how long it has run and the landing page it sends traffic to.',
          'All of it is searchable once the ad is in your swipe file.'
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'ai-tab',
        target: 'drawer-tab-ai',
        cardPlacement: 'left',
        title: 'Open AI Analysis',
        body: 'Click the tab to see the breakdown Foreplay wrote for this ad.',
        advance: 'click-target'
      },
      {
        id: 'ai-analysis',
        target: 'drawer-ai-analysis',
        cardPlacement: 'left',
        title: 'Why the ad works',
        body: [
          'The hook, the angle, the emotional drivers and the call to action, spelled out.',
          'This is generated for every ad you save.'
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'transcript-tab',
        target: 'drawer-tab-transcript',
        cardPlacement: 'left',
        title: 'Open the Transcript',
        body: 'Click the tab to read what the ad actually says.',
        advance: 'click-target'
      },
      {
        id: 'transcript',
        target: 'drawer-transcript',
        cardPlacement: 'left',
        title: 'Every word, timestamped',
        body: [
          'Videos are transcribed automatically. Click a line to jump the video to that moment.',
          'A month from now you can search your swipe file by what the ads say.'
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'downloads',
        target: 'drawer-downloads',
        cardPlacement: 'left',
        title: 'Take the asset with you',
        body: 'Download the video or just the thumbnail. Handy for briefs and decks.',
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'details-tab',
        target: 'drawer-tab-details',
        cardPlacement: 'left',
        title: 'Back to Details',
        body: 'Saving lives on the Details tab. Click it to head back.',
        advance: 'click-target'
      },
      {
        id: 'save',
        target: 'drawer-save',
        cardPlacement: 'left',
        title: 'Save it',
        body: "Add this one to your swipe file. That's the habit everything else in Foreplay builds on.",
        advance: 'click-target',
        hint: 'Click Save to continue'
      }
    ]
  },

  // Plays once the showcase ad's save lands (the drawer closes first)
  'save-ad-done': {
    id: 'save-ad-done',
    returnToSetup: true,
    completesStep: 'first_ad_saved',
    steps: [
      {
        id: 'done',
        target: null,
        variant: 'completion',
        scene: 'ad-saved',
        pill: 'Saved to your swipe file',
        accent: 'blue',
        kicker: 'Up next: Boards',
        title: 'Your swipe file has its first ad',
        body: [
          'The transcript, the AI breakdown and the landing page came with it.',
          'Next, learn how to organize your ads with boards and share them.'
        ],
        advance: 'next',
        ctaLabel: 'Back to Launchpad'
      }
    ]
  },

  // Sidebar create tip → create-board modal → seed ads → share the board
  'board-share': {
    id: 'board-share',
    returnToSetup: true,
    completesStep: 'board_shared',
    steps: [
      {
        id: 'sidebar-create',
        target: 'sidebar-create-new',
        optionalTargets: ['sidebar-create-dropdown'],
        passthrough: true,
        openSidebarCreate: true,
        cardPlacement: 'right',
        title: 'Create from the sidebar',
        body: [
          'Create Board and Create Folder live on the + button anytime.',
          'Boards hold ads; folders keep the sidebar tidy.'
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'name',
        target: 'board-name',
        interactive: true,
        cardPlacement: 'right',
        title: 'Name your board',
        body: [
          'Type a name, or tap one of the suggestions.',
          'Boards hold saved ads: a campaign, a client, a format you keep coming back to.'
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'folder',
        target: 'board-folder',
        cardPlacement: 'right',
        title: 'Boards live in folders',
        body: [
          'Group boards by client or team so the sidebar stays tidy.',
          'The default folder is fine for now.'
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'create',
        target: 'board-create',
        cardPlacement: 'above',
        title: 'Create it',
        body: 'Click Create Board. We take it from here.',
        advance: 'event',
        event: 'setup:board-created',
        hint: 'Click Create Board to continue'
      },
      {
        id: 'seed',
        target: 'board-seed',
        cardPlacement: 'right',
        title: "We'll add these for you",
        body: [
          "A few ads from {brandName} so the board isn't empty when you share it.",
          'Confirm and they land in {boardName}.'
        ],
        advance: 'event',
        event: 'setup:board-seeded',
        hint: 'Confirm to continue'
      },
      {
        id: 'share',
        target: 'board-share',
        title: 'Share it',
        body: [
          'Click Share to copy the link.',
          "Viewers don't need a Foreplay account, and the board updates as you add to it."
        ],
        advance: 'click-target',
        hint: 'Click Share to continue'
      },
      {
        id: 'done',
        target: null,
        variant: 'completion',
        scene: 'board-shared',
        pill: 'Link copied',
        accent: 'blue',
        kicker: 'Up next: Discovery',
        title: 'Send it anywhere',
        body: 'Paste the link to a teammate, a client or a creator. They see the live board, always current.',
        advance: 'next',
        ctaLabel: 'Back to Launchpad'
      }
    ]
  },

  // After a brand is tracked from the picker: tour of its Spyder brand page
  'spyder-brand': {
    id: 'spyder-brand',
    returnToSetup: true,
    completesStep: 'spyder_track',
    steps: [
      {
        id: 'library',
        target: 'spyder-library',
        waitForReady: 'spyder-analytics',
        readyLabel: "Pulling in {brandName}'s analytics…",
        cardPlacement: 'center',
        route: { name: 'SpyderView', params: { id: '{brandId}', view: 'meta', mode: 'ad-library' } },
        title: 'Every ad they run, plus the numbers behind it',
        body: [
          "This is {brandName}'s complete ad library. New ads land here automatically and stay after Meta pulls them.",
          'Up top, Analytics shows their live media mix, the landing pages they send traffic to, and the hooks that have run longest.'
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'filters',
        targets: ['spyder-filters', 'spyder-data-since'],
        title: 'Cut it down to what matters',
        body: [
          'Filter by format, platform, status or date. Sort by longest running to surface proven winners.',
          "Spyder has collected {brandName}'s ads since the date shown up top. You get the whole history, not just what's live today."
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'modes',
        target: 'spyder-modes',
        cardPlacement: 'right',
        title: 'Every view answers a different question',
        items: [
          { label: 'Ad Rank', text: 'Which ads carry the account, ranked by share of impressions.' },
          { label: 'Creative Tests', text: 'What they\'re iterating on right now, grouped by concept.' },
          { label: 'Landing Pages', text: 'Where the traffic goes and which pages get the most ads.' },
          { label: 'Hooks', text: 'Every opening line they\'ve run, ranked by how long it stayed live.' },
          { label: 'Partnership Ads', text: 'The creators and affiliates running ads on their behalf.' }
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'done',
        target: null,
        variant: 'completion',
        scene: 'brand-tracked',
        pill: 'Brand tracked',
        accent: 'red',
        kicker: 'Up next: Ad Rank',
        title: "You're tracking {brandName}",
        body: [
          'New ads show up from here on without you checking.',
          "We've ticked this step off. Next, see which of these ads actually carry the account."
        ],
        advance: 'next',
        ctaLabel: 'Back to Launchpad'
      }
    ]
  },

  // Ad Rank reveal: starts on the tour brand's Overview, follows the user into the full rankings
  'ad-rank': {
    id: 'ad-rank',
    returnToSetup: true,
    completesStep: 'ad_rank_opened',
    steps: [
      {
        id: 'preview',
        target: 'adrank-preview',
        waitForReady: true,
        readyLabel: "Ranking {brandName}'s ads…",
        route: { name: 'SpyderView', params: { id: '{brandId}', view: 'overview' } },
        title: "{brandName}'s top ranked ads, right now",
        body: [
          "Each card is ranked by its share of the brand's total impressions.",
          'Rank 1 is the ad carrying the account today.'
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'view-all',
        target: 'adrank-view-all',
        title: 'See the full ranking',
        body: 'Click View All Rankings to open the Ad Rank view.',
        advance: 'click-target'
      },
      {
        id: 'graph',
        target: 'adrank-graph',
        title: 'The Top 100, plotted',
        body: [
          'Every dot is one ad. Higher means a bigger share of impressions. Further right means it has been live longer.',
          'Click any dot to open that ad.'
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'categories',
        target: 'adrank-categories',
        title: 'Breakout vs. Evergreen',
        body: [
          'Breakout ads are climbing fast right now: new creative the brand is scaling.',
          'Evergreen ads have held a top spot for months. Those are the proven winners.'
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'badge',
        target: 'ad-rank-badge',
        optionalTargets: ['ad-rank-tooltip'],
        cardPlacement: 'right',
        title: "Hover the #1 ad's rank",
        body: [
          'Every ranked ad carries its position and how it has moved.',
          'Hover the rank to see the trend, then save anything worth stealing straight from here.'
        ],
        advance: 'hover-target',
        hint: 'Hover the rank to continue',
        ctaLabel: 'Next'
      },
      {
        id: 'done',
        target: null,
        variant: 'completion',
        scene: 'ad-rank',
        pill: 'Ad Rank unlocked',
        accent: 'red',
        kicker: 'Up next: Save your first ad',
        title: "You know what's winning for {brandName}",
        body: [
          "The ranking updates as the brand's spend shifts. Come back any day.",
          'Next, save an ad and see everything Foreplay extracts from it.'
        ],
        advance: 'next',
        ctaLabel: 'Back to Launchpad'
      }
    ]
  },

  // Lens creation flow on LensView: proceed → name + currency → authenticate + create → completion.
  // LensView opens the creation modal on the proceed step and skips to the
  // completion card when a Lens brand already exists. (The branch's paywall
  // step and demo-report copy return with the phase 2 Lens demo.)
  'lens-connect': {
    id: 'lens-connect',
    returnToSetup: true,
    completesStep: 'lens_connect',
    steps: [
      {
        id: 'proceed',
        target: 'lens-connect-proceed',
        route: { name: 'LensView' },
        cardPlacement: 'above',
        title: 'One brand, many accounts',
        body: 'This is the Lens brand. Ad accounts get attached on the next screen. Click Next.',
        advance: 'click-target',
        hint: 'Click Next to continue'
      },
      {
        id: 'details',
        target: 'lens-connect-name',
        optionalTargets: ['lens-connect-currency'],
        interactive: true,
        passthrough: true,
        cardPlacement: 'left',
        title: 'Name it, then set currency',
        body: [
          'The brand name is what you will see in the sidebar.',
          'Currency is picked from the accounts you attach. Timezone is stored with the brand for reporting windows.'
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'auth',
        target: 'lens-connect-auth',
        optionalTargets: ['lens-connect-submit'],
        passthrough: true,
        cardPlacement: 'left',
        title: 'Authenticate Meta or TikTok',
        body: [
          'Connect the ad account, then create the Lens brand.',
          'The first sync usually takes 24–48 hours.'
        ],
        advance: 'event',
        event: 'setup:lens-created',
        hint: 'Authenticate and create the Lens to continue'
      },
      {
        id: 'done',
        target: null,
        variant: 'completion',
        scene: 'lens-connected',
        pill: 'Account connected',
        accent: 'black',
        kicker: 'First sync: 24–48 hours',
        title: 'Lens is syncing your ads',
        body: [
          'Most accounts are ready in 24–48 hours. Nobody else can see this data.',
          'Your reports fill in as the sync completes.'
        ],
        advance: 'next',
        ctaLabel: 'Back to Launchpad'
      }
    ]
  },

  // Demo Lens: Top Performing report on /lens/demo (SetupView enters the demo first)
  'lens-top-performing': {
    id: 'lens-top-performing',
    returnToSetup: true,
    completesStep: 'lens_top_performing',
    steps: [
      {
        id: 'when',
        target: null,
        route: { name: 'LensReport', params: { lensId: 'demo', reportId: 'demo-top-performing' } },
        title: 'When to use Top Performing',
        body: 'Create Top Performing reports when you want to group ads by a single dimension (Ad Name, Creative, Copy, etc).',
        waitForReady: 'lens-filter-toolbar',
        readyLabel: 'Loading the demo report…',
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'group-by',
        targets: ['lens-group-by', 'lens-ad-accounts'],
        openGroupBy: true,
        cardPlacement: 'below',
        title: 'Group your ads',
        body: [
          'Group ads by Ad Name, Creative, Copy, Landing Page, etc.',
          'If you have multiple Meta ad accounts under one Lens, you can blend this grouping.'
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'graph-type',
        target: 'lens-graph-grid',
        optionalTargets: ['lens-graph-type'],
        cardPlacement: 'below',
        title: 'Change your visualization',
        body: 'Bar chart compares rows, line shows the trend, and grid/card view is the most visual creative breakdown.',
        advance: 'click-target',
        hint: 'Click the card view'
      },
      {
        id: 'add-rows',
        target: 'lens-row-checkbox',
        matchAll: true,
        cardPlacement: 'right',
        title: 'Add ads to the card view',
        body: 'Click the checkboxes on the rows to add more ads to the visualization.',
        advance: 'click-target',
        hint: 'Click a checkbox to continue'
      },
      {
        id: 'filter',
        target: 'lens-add-filter',
        optionalTargets: ['lens-add-filter-dropdown'],
        passthrough: true,
        cardPlacement: 'below',
        title: 'Filter your report',
        body: 'You can make unlimited reports, so feel free to filter, slice and dice the ads displayed by any metric or dimension in your ad account.',
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'columns',
        target: 'lens-customize-columns',
        cardPlacement: 'below',
        title: 'Customize Columns',
        body: 'Click to pick the metrics on the table. Save the set as a preset so every report starts the same way.',
        advance: 'click-target',
        hint: 'Click Customize Columns'
      },
      {
        id: 'settings',
        target: 'lens-settings-drawer',
        waitForReady: true,
        readyLabel: 'Opening column settings…',
        cardPlacement: 'left',
        title: 'Search every Meta metric',
        body: [
          'All your Meta metrics are searchable here and can be added to a report.',
          'This includes calculated Foreplay Scores and custom conversions you have set up in your account.'
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'done',
        target: null,
        variant: 'completion',
        scene: 'lens-report',
        pill: 'Top Performing unlocked',
        accent: 'black',
        kicker: 'Up next: Comparison',
        title: 'You can create & understand a Top Performing report',
        body: [
          'This was a demo on Foreplay ads. The same layout is what your account uses after it syncs.',
          'Next, learn how to compare agency, creators, and in-house in a Comparison report.'
        ],
        advance: 'next',
        ctaLabel: 'Back to Launchpad'
      }
    ]
  },

  // Demo Lens: Comparison report
  'lens-comparison': {
    id: 'lens-comparison',
    returnToSetup: true,
    completesStep: 'lens_comparison',
    steps: [
      {
        id: 'when',
        target: null,
        route: { name: 'LensReport', params: { lensId: 'demo', reportId: 'demo-comparative' } },
        title: 'When to use Comparison',
        body: [
          'Use this when the question is which source is winning — agency, external creators, or in-house.',
          'The convention is tokens in the ad name: one group contains agency, one contains creator, and Not agency is everything else. The same report can also split by format, hook, or prospecting vs retargeting.'
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'groups',
        targets: ['lens-add-group', 'lens-group-name'],
        waitForReady: true,
        readyLabel: 'Loading comparison groups…',
        interactive: true,
        passthrough: true,
        cardPlacement: 'below',
        title: 'Add or name a group',
        body: [
          'These three groups are already on the page. Agency and External creator are contains filters. Not agency is the complement — does not contain agency and does not contain creator — so the buckets do not overlap.',
          'Click a name to rename it. Add Group to start a new one.'
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'comparing',
        target: 'lens-comparing',
        cardPlacement: 'below',
        title: 'Comparing N groups',
        body: 'This chip is the group builder for Agency, External creator, and Not agency. Open it anytime to add, rename or remove groups without leaving the report.',
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'breakdown',
        target: 'lens-row-breakdown',
        cardPlacement: 'right',
        title: 'See the ads inside a group',
        body: 'Click the group details icon on a table row to open every ad that rolled up into that group.',
        advance: 'click-target',
        hint: 'Click the group details icon to continue'
      },
      {
        id: 'drawer',
        target: 'lens-group-breakdown',
        waitForReady: true,
        readyLabel: 'Opening the ads in this group…',
        cardPlacement: 'left',
        title: 'The ads behind the number',
        body: [
          'This is the group, exploded into creatives.',
          'Use it when someone asks "which ads are the agency\'s?" and you want the list, not a guess.'
        ],
        advance: 'next',
        ctaLabel: 'Next'
      },
      {
        id: 'done',
        target: null,
        variant: 'completion',
        scene: 'lens-report',
        pill: 'Comparison unlocked',
        accent: 'black',
        kicker: 'Up next: Your own account',
        title: 'You can compare more than two ideas',
        body: [
          'These groups are dummy data. Putting agency or creator in the ad name is what fills them on a real account.',
          'Connect your own ad account to compare your real groups.'
        ],
        advance: 'next',
        ctaLabel: 'Back to Launchpad'
      }
    ]
  }
}
